/**
 * Token Validator Utility
 * 
 * Validates JWT tokens client-side, same as new_demo_frontend's /api/validateToken
 * This matches the approach used in new_demo_frontend where tokens are validated
 * by decoding the JWT payload without making a backend call.
 */

import { jwtDecode } from 'jwt-decode';

interface TokenPayload {
  id: string;
  username: string;
  name: string;
  title: string;
  group: string;
  groupName: string;
  email: string;
  permissions: string;
  exp?: number;
  iat?: number;
}

/**
 * Validates a JWT token by decoding it
 * Returns user data if token is valid, null otherwise
 * Same approach as new_demo_frontend's /api/validateToken
 */
export function validateToken(token: string): {
  group: string;
  groupName: string;
  title: string;
  name: string;
  id: string;
  username: string;
  email: string;
  permissions: string;
} | null {
  try {
    if (!token) {
      return null;
    }

    // Decode the token (jwt-decode doesn't verify signature, but checks expiration)
    const decoded = jwtDecode<TokenPayload>(token);

    // Check if token is expired
    if (decoded.exp && decoded.exp * 1000 < Date.now()) {
      console.warn('Token is expired');
      return null;
    }

    // Extract user information from token payload
    const { group, groupName, title, name, id, username, email, permissions } = decoded;

    if (!id || !username) {
      console.warn('Token missing required fields');
      return null;
    }

    return {
      group,
      groupName,
      title,
      name,
      id,
      username,
      email,
      permissions,
    };
  } catch (error) {
    console.error('Error validating token:', error);
    return null;
  }
}

